import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { MascotImageFrame, MascotEyes, MascotSize, MascotAnimation } from '../models/mascot.types';

@Component({
  selector: 'lib-ui-mascot',
  templateUrl: './ui-mascot.component.html',
  styleUrl: './ui-mascot.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiMascotComponent {
   readonly imageSrc = input.required<string>();
  readonly imageAlt = input<string>('Mascote');
  readonly images = input<readonly MascotImageFrame[]>([]);
  readonly imageIndex = input<number>(0);

  readonly eyes = input<MascotEyes>('default');
  readonly size = input<MascotSize>('md');
  readonly animation = input<MascotAnimation>('idle');

  readonly thoughtEmoji = input<string | null>(null);
  readonly thoughtText = input<string | null>(null);
  readonly thoughtVisible = input<boolean>(false);

  readonly autoCycleImages = input<boolean>(false);
  readonly cycleIntervalMs = input<number>(2800);
  readonly toggleThoughtOnClick = input<boolean>(true);

  readonly mascotPressed = output<void>();

  private readonly localImageIndex = signal(0);
  private readonly localThoughtVisible = signal(false);

  protected readonly resolvedImage = computed(() => {
    const frames = this.images();

    if (!frames.length) {
      return {
        src: this.imageSrc(),
        alt: this.imageAlt(),
        eyes: this.eyes(),
      };
    }

    const requestedIndex = this.imageIndex();
    const localIndex = this.localImageIndex();
    const index = this.autoCycleImages() ? localIndex : requestedIndex;
    const safeIndex = Math.abs(index) % frames.length;

    return frames[safeIndex];
  });

  protected readonly resolvedEyes = computed(
    () => this.resolvedImage().eyes ?? this.eyes()
  );

  protected readonly hasThought = computed(
    () => !!this.thoughtEmoji() || !!this.thoughtText()
  );

  protected readonly shouldShowThought = computed(
    () =>
      this.hasThought() &&
      (this.thoughtVisible() || this.localThoughtVisible())
  );

  private readonly cycleEffect = effect((onCleanup) => {
    if (!this.autoCycleImages() || this.images().length < 2) {
      return;
    }

    const intervalId = globalThis.setInterval(() => {
      this.localImageIndex.update((current) => current + 1);
    }, this.cycleIntervalMs());

    onCleanup(() => {
      globalThis.clearInterval(intervalId);
    });
  });

  protected onMascotClick(): void {
    if (this.toggleThoughtOnClick()) {
      this.localThoughtVisible.update((value) => !value);
    }

    this.mascotPressed.emit();
  }
}
